<template>
  <div v-if="!ready" v-loading="!ready" element-loading-text="拼命加载中" class="page-loading"></div>
  <div v-else>
    <div class="flex">
      <div class="left">
        <slot name="title"></slot>
        <el-tabs v-model="active_index">
          <el-tab-pane v-for="(department, index) in departments" :key="department.uid" :name="index+''"
                       :label="`${department.name}(${department.collections.length})`">
          </el-tab-pane>
        </el-tabs>
      </div>
      <div class="right">
        <el-input placeholder="搜索任务" class="max-width-200 w-a"
                  suffix-icon="el-icon-search" v-model="search"></el-input>
        <router-link v-if="$location.admin_collection_edit.auth()"
                     :to="$location.admin_collection_edit({department_uid: active_department.uid})"
                     class="el-button el-button--success">
          <icon name="plus"></icon>
          创建新任务
        </router-link>
      </div>
    </div>
    <el-table :data="page" border ref="table">
      <el-table-column label="状态" prop="status">
        <template slot-scope="scope">
          <span :class="{'text-danger':scope.row.status === '禁用'}">{{status_map[scope.row.status]}}</span>
        </template>
      </el-table-column>
      <el-table-column label="任务名称" prop="name">
        <template slot-scope="scope">
          <el-tooltip>
            <div class="oneline">
              <span v-html="$highlight(scope.row.name, scope.row.$$pinyin.name)"></span>
            </div>
            <div slot="content">
              <span v-html="$highlight(scope.row.name, scope.row.$$pinyin.name)"></span>
            </div>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="具体要求" prop="desc">
        <template slot-scope="scope">
          <el-tooltip placement="bottom">
            <div class="oneline">{{scope.row.desc}}</div>
            <div slot="content" class="text-pre-wrap">{{scope.row.desc}}</div>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="附件" prop="file.name">
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.file" :content="scope.row.file.name">
            <div class="oneline">
              <a class="link-success" :href="scope.row.file.url" :download="scope.row.file.name" target="_blank">
                <icon :name="scope.row.file.name | fileicon"></icon>
                {{scope.row.file.name}}
              </a>
            </div>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="创建日期" prop="time_create">
        <template slot-scope="scope">{{scope.row.time_create | sub(0, -3)}}</template>
      </el-table-column>
      <el-table-column label="提交人数" prop="total"></el-table-column>
      <el-table-column label="操作" width="180" align="center" class-name="no-padding">
        <template slot-scope="scope">
          <router-link v-if="$location.admin_collection_view.auth()"
                       :to="$location.admin_collection_view({collection_uid: scope.row.uid})"
                       class="el-button el-button--primary el-button--mini">查看
          </router-link>
          <router-link v-if="$location.admin_collection_edit.auth()"
                       :to="$location.admin_collection_edit({collection_uid: scope.row.uid})"
                       class="el-button el-button--primary el-button--mini">编辑
          </router-link>
          <el-button type="warning" size="mini" @click="toggleDisabled(scope.row)"
                     :disabled="scope.row.$$loading>0">
            {{scope.row.status === '禁用' ? '启用' : '禁用'}}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination v-if="filtered.length>pageInfo.page" :total="filtered.length"
                   :page-size="pageInfo.page" :current-page.sync="pageInfo.current"
                   class="text-center m-t-10" layout="prev, pager, next"></el-pagination>
  </div>
</template>

<script>
import mapValues from 'lodash/mapValues'
import keyBy from 'lodash/keyBy'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import assign from 'lodash/assign'

import * as Collection from 'api/collection'

import page from 'utils/page'

export default {
  data () {
    return {
      list: null,
      departments: null,
      ready: false,
      pageInfo: {page: 10, current: 1},
      active_index: '0',
      search: '',
    }
  },
  computed: {
    active_department () {
      return this.departments[this.active_index]
    },
    active_collections () {
      return this.active_department ? this.active_department.collections : []
    },
    filtered () {
      return this.$search(this.active_collections, this.search, ['name'])
    },
    page () {
      return page(this.filtered, this.pageInfo)
    },
  },
  methods: {
    init () {
      this.ready = false
      this.$guard()(async () => {
        let [list, departments, status] = await Promise.all([
          Collection.list(),
          Collection.user_department_list({user_uid: this.$user.uid}),
          Collection.status(),
        ])
        list = orderBy(list, 'time_create', 'desc')
        let group = groupBy(list, 'department_uid')
        this.active_index = '0'
        departments.forEach((department, index) => {
          department.collections = group[department.uid] || []
          if (department.uid === this.$query.department_uid) this.active_index = index + ''
        })
        this.status_map = mapValues(keyBy(status, 'id'), 'name')
        this.group = group
        this.list = list
        this.departments = departments
        this.ready = true
      })
    },
    doInit () {
      this.pageInfo.current = 1
      this.$updateQuery({department_uid: this.departments[this.active_index].uid})
    },
    async remove (item) {
      if (item.$$loading) return
      let collections = this.active_collections
      if (!await this.$dangerConfirm('您确定要删除该任务吗？')) return
      this.$guard(item, '$$loading')(async () => {
        await Collection.remove(item)
        let idx = collections.indexOf(item)
        collections.splice(idx, 1)
        this.$message.success('删除成功')
      })
    },
    async toggleDisabled (item) {
      if (item.$$loading) return
      if (item.status !== '禁用' && !await this.$confirm('您确定要禁用该任务吗？', '提示')) return
      this.$guard(item, '$$loading')(async () => {
        let data = await Collection.toggle_disable(item)
        this.$message.success(item.status !== '禁用' ? '禁用成功' : '启用成功')
        assign(item, data)
      })
    },
  },
  created () {
    this.init()
  },
  watch: {
    $route () {
      if (!this.$dontReload) this.init()
    },
    active_index: 'doInit',
  },
}
</script>

<style lang="scss" scoped>
.flex {
  display: flex;
  align-items: flex-end;
  .left {
    flex: 1;
    padding-right: 10px;
  }
  .right {
    padding: 15px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/collection/AdminCollectionList.vue